"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { Mail, Phone, MessageSquare } from "lucide-react"

const services = ["E-commerce", "Landing Page", "Gerenciador de conteúdo", "Criação de interface (UX/UI)", "Outro"]

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
    isWhatsapp: true,
    privacyPolicy: false,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        alert("Mensagem enviada com sucesso!")
        setFormData({
          name: "",
          phone: "",
          email: "",
          subject: "",
          message: "",
          isWhatsapp: true,
          privacyPolicy: false,
        })
      } else {
        alert("Erro ao enviar mensagem. Tente novamente.")
      }
    } catch (error) {
      console.error("Erro:", error)
      alert("Erro ao enviar mensagem. Tente novamente.")
    }
  }

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <section id="contact" className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/50">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Entre em Contato</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Pronto para transformar suas ideias em realidade? Agende sua consultoria gratuita!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-6">Fale Conosco</h3>
              <div className="space-y-4">
                {/*<div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-accent" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Email</p>
                    <p className="text-slate-300">contato@voidagencia.com</p>
                  </div>
                </div>*/}

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-accent" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-medium text-white">WhatsApp</p>
                    <p className="text-slate-300">(51) 98320-4177</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-accent" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Consultoria Gratuita</p>
                    <p className="text-slate-300">Agende uma conversa sem compromisso</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle>Envie sua Mensagem</CardTitle>
              <CardDescription>Preencha o formulário e entraremos em contato em breve</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    placeholder="Seu nome completo"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    required
                    aria-describedby="name-error"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Telefone *</Label>
                  <Input
                    id="phone"
                    placeholder="(51) 98320-4177"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    required
                    aria-describedby="phone-error"
                  />
                  <div className="flex items-center space-x-2 mt-2">
                    <Checkbox
                      id="whatsapp"
                      checked={formData.isWhatsapp}
                      onCheckedChange={(checked) => handleInputChange("isWhatsapp", checked as boolean)}
                    />
                    <Label htmlFor="whatsapp" className="text-sm">
                      Mesmo número do WhatsApp
                    </Label>
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">E-mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu_email@mail.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    required
                    aria-describedby="email-error"
                  />
                </div>

                <div>
                  <Label htmlFor="subject">Assunto *</Label>
                  <Select onValueChange={(value) => handleInputChange("subject", value)} required>
                    <SelectTrigger aria-label="Selecione um serviço">
                      <SelectValue placeholder="Selecione um serviço" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message">Mensagem *</Label>
                  <Textarea
                    id="message"
                    placeholder="Digite sua mensagem aqui..."
                    value={formData.message}
                    onChange={(e) => handleInputChange("message", e.target.value)}
                    required
                    aria-describedby="message-error"
                  />
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="privacy"
                    checked={formData.privacyPolicy}
                    onCheckedChange={(checked) => handleInputChange("privacyPolicy", checked as boolean)}
                    required
                  />
                  <Label htmlFor="privacy" className="text-sm leading-relaxed">
                    Eu aceito as políticas de privacidade e os termos de uso
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white font-medium border-0"
                  aria-label="Enviar mensagem de contato"
                >
                  Enviar Mensagem
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}
